import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { UserCheck, Shield } from 'lucide-react';
import { ProxyUserSearchModal } from './ProxyUserSearchModal';
import { useProxy } from '@/contexts/ProxyContext';
import { usePermissions } from '@/hooks/usePermissions';

interface ProxyButtonProps {
  variant?: 'header' | 'sidebar' | 'admin';
  className?: string;
}

export function ProxyButton({ variant = 'header', className }: ProxyButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isProxyMode } = useProxy();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();

  // Check if user has proxy permissions - support both old and new permission names
  const canProxyUsers = hasPermission('proxy_users') || 
                     hasPermission('can_proxy_users') || 
                     hasPermission('can_proxy_any_user');
  
  // Don't show button if user doesn't have permissions or is already in proxy mode
  if (permissionsLoading) {
    return null; // Don't render while loading
  }

  if (!canProxyUsers || isProxyMode) {
    return null;
  }

  const getButtonContent = () => {
    switch (variant) {
      case 'header':
        return (
          <>
            <UserCheck className="h-4 w-4 mr-2" />
            Proxy User
          </>
        );
      case 'sidebar':
        return (
          <>
            <Shield className="h-4 w-4" />
            <span className="ml-2">Proxy Mode</span>
          </>
        );
      case 'admin':
        return (
          <>
            <UserCheck className="h-4 w-4 mr-2" />
            Start Proxy Session
          </>
        );
      default:
        return (
          <>
            <UserCheck className="h-4 w-4 mr-2" />
            Proxy User
          </>
        );
    }
  };

  const getButtonStyles = () => {
    switch (variant) {
      case 'header':
        return 'h-8 px-3 text-sm';
      case 'sidebar':
        return 'w-full justify-start';
      case 'admin':
        return 'w-full sm:w-auto';
      default:
        return '';
    }
  };

  return (
    <>
      <Button
        variant={variant === 'sidebar' ? 'ghost' : 'outline'}
        size={variant === 'header' ? 'sm' : 'default'}
        onClick={(e) => {
          // Prevent event bubbling to parent dropdown
          e.preventDefault();
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className={`${getButtonStyles()} ${className}`}
        aria-label="Start proxy session to access another user's account"
      >
        {getButtonContent()}
      </Button>

      <ProxyUserSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
